import i18next from "i18next";
import {
	FILTER_SEPARATOR,
	FILTER_VALUE_SEPARATOR,
	NEW_WAY_TO_ENCODING_FILTER_SIGN,
} from "go-list/core/components/Filter/services";
import isPredefinedRange, { parsePredefinedRangeToDefaultString } from "go-list/utils/daterangeutils";
import { ReportApi } from "go-report/core/services/types";
import { UserState } from "go-security/services/users/types";
import {
	DASHBOARD_RESOURCE_TYPE,
	DEFAULT_DASHBOARD_SEGMENT_VALUE,
	GROUP_DASHBOARD_RESOURCE_TYPE,
	ORDER_OF_BASIC_DASHBOARD_ITEMS,
	ORGANIZATION_DASHBOARD_DEFAULT_LG_LAYOUT,
	USER_DASHBOARD_DEFAULT_LG_LAYOUT,
	getDashboardSegmentNonMobileConfigForSegment,
} from "../services/segments/constants";
import { CallRecorderData, DashboardType, SeeMoreLinkConfig } from "../services/types";
import { parsePredefinedRangeToPreviousPeriodRange } from "./reportConfigUtils";

export const getDefaultDashboardSegment = (isGroupDashboard: boolean, user?: UserState) => {
	const segmentListAndResourceType = isGroupDashboard ? GROUP_DASHBOARD_RESOURCE_TYPE : DASHBOARD_RESOURCE_TYPE;

	const defaultDashboardSegment = {
		id: "0",
		name: i18next.t("go_segment.field.default.title", { ns: "lib" }),
		slug: "default",
		default: true,
		value: DEFAULT_DASHBOARD_SEGMENT_VALUE,
		list_name: segmentListAndResourceType,
		resource_type: segmentListAndResourceType,
		status: "ENABLED",
	};

	defaultDashboardSegment.value.dashboardEdit.nonMobileGridConfig = getDashboardSegmentNonMobileConfigForSegment(
		isGroupDashboard ? USER_DASHBOARD_DEFAULT_LG_LAYOUT : ORGANIZATION_DASHBOARD_DEFAULT_LG_LAYOUT
	);

	if (isGroupDashboard) {
		const organizationIds = user?.organizations
			?.map((organization) => organization.id)
			.sort()
			.join(FILTER_VALUE_SEPARATOR);

		return {
			...defaultDashboardSegment,
			value: {
				...DEFAULT_DASHBOARD_SEGMENT_VALUE,
				filtersConfig: {
					...DEFAULT_DASHBOARD_SEGMENT_VALUE.filtersConfig,
					selectedFilter: {
						...DEFAULT_DASHBOARD_SEGMENT_VALUE.filtersConfig.selectedFilter,
						organizationIds: organizationIds ?? "",
					},
				},
			},
		};
	}

	return defaultDashboardSegment;
};

export const getSeeMoreLink = ({
	dateRange,
	reportName,
	organizationId,
	withCompareParam,
	customFilters,
	customReportPath,
	customGroups,
}: SeeMoreLinkConfig) => {
	let link = "";

	if (customReportPath) link += customReportPath;
	else if (organizationId) link += `${organizationId}/reports/${reportName}?`;
	else link += `/group_reports/${reportName}?`;

	let filterParam = customFilters || dateRange ? `${NEW_WAY_TO_ENCODING_FILTER_SIGN}` : "";

	if (dateRange) {
		const dateIsPredefinedRange = isPredefinedRange(dateRange);
		let dateParam = "date_range|bt=";

		if (dateIsPredefinedRange) dateParam += parsePredefinedRangeToDefaultString(dateRange);
		else dateParam += dateRange.replace(",", FILTER_VALUE_SEPARATOR);

		filterParam += dateParam;

		if (withCompareParam) {
			const dateCompareParam = `${FILTER_SEPARATOR}compare_date_range|bt=${parsePredefinedRangeToPreviousPeriodRange(
				dateRange,
				true
			)}`;
			filterParam += dateCompareParam;
		}
	}

	if (customFilters) {
		filterParam += customFilters;
	}

	if (filterParam) {
		link += `f=${btoa(unescape(encodeURIComponent(filterParam)))}`;
	}

	if (customGroups) {
		link += `&groups=${customGroups}`;
	}

	return link;
};

export const getCallRecorderData = (
	callsReportCurrentDateRange?: ReportApi,
	callsReportPreviousDateRange?: ReportApi
): CallRecorderData => {
	const callsReportData = callsReportCurrentDateRange?.reports?.[0]?.sub_report;
	const callsReportPreviousPreiodData = callsReportPreviousDateRange?.reports?.[0]?.sub_report;

	const answeredCallsData = callsReportData?.find((reportData) => reportData.group_by_value.name === "RECEIVED");
	const missedCallsData = callsReportData?.find((reportData) => reportData.group_by_value.name === "MISSED");
	const previousPeriodAnsweredCallsData = callsReportPreviousPreiodData?.find(
		(reportData) => reportData.group_by_value.name === "RECEIVED"
	);
	const previousPeriodMissedCallsData = callsReportPreviousPreiodData?.find(
		(reportData) => reportData.group_by_value.name === "MISSED"
	);

	const answeredCalls = answeredCallsData?.aggregate?.summary?.amount ?? 0;
	const missedCalls = missedCallsData?.aggregate?.summary?.amount ?? 0;
	const previousPeriodAnsweredCalls = previousPeriodAnsweredCallsData?.aggregate?.summary?.amount ?? 0;
	const previousPeriodMissedCalls = previousPeriodMissedCallsData?.aggregate?.summary?.amount ?? 0;

	return { answeredCalls, missedCalls, previousPeriodAnsweredCalls, previousPeriodMissedCalls };
};

export const shouldDisplayCallRecorderBox = (dashboardType: DashboardType, installedAppProviders?: string[]) =>
	dashboardType === DashboardType.ORGANIZATION && installedAppProviders?.includes("CALL_RECORDER");

export const getOrderOfDashboardItems = (dashboardType: DashboardType, installedAppProviders?: string[]) => {
	const dashboardItems = ORDER_OF_BASIC_DASHBOARD_ITEMS;

	if (shouldDisplayCallRecorderBox(dashboardType, installedAppProviders)) {
		const clientDetailsBoxIndex = ORDER_OF_BASIC_DASHBOARD_ITEMS.findIndex(
			(dashboardItem: string) => dashboardItem === "CLIENT_DETAILS"
		);
		if (clientDetailsBoxIndex !== -1) {
			dashboardItems.splice(clientDetailsBoxIndex + 1, 0, "CALL_RECORDER");
		}
	}

	return dashboardItems;
};
